import React, {PureComponent, Fragment} from 'react';
import {connect} from 'react-redux';
import {compose} from 'redux';
import cx from 'classnames';
import pt, {arrayOf, shape} from 'prop-types';

import Video from '../../components/video/Video';
import DynamicLogo from '../../components/dynamic-logo/DynamicLogo';
import Button from '../button/Button';
import Picture from '../picture/Picture';
import {WithWindow} from '../../hocs';

import {
    breakpoints,
    video as videoConstant,
    logoContainerTypes,
    CustomPropShapes,
    imageOrientation
} from '../../constants';
import {getAssetPath} from '../../utils/DieselUtils';
import {selectIsLauncher} from '../../redux/selectors/app.selectors';

import styles from './Hero.pcss';

const mapStateToProps = state => ({
    isLauncher: selectIsLauncher(state)
});

class Hero extends PureComponent {
    static propTypes = {
        ...WithWindow.propTypes,
        altContentPosition: pt.bool,
        button: shape({
            isExternal: pt.bool,
            onClick: pt.func,
            text: pt.string.isRequired,
            theme: pt.string,
            url: pt.string
        }),
        hasFortniteFont: pt.bool,
        inFeed: pt.bool,
        isFullBleed: pt.bool,
        isLauncher: pt.bool,
        locale: pt.string,
        logoAltText: pt.string,
        logoSrc: pt.string,
        rating: pt.array,
        srcSet: shape({
            [imageOrientation.HORIZONTAL]: pt.string,
            [imageOrientation.VERTICAL]: pt.string
        }),
        subtitle: pt.string,
        title: pt.string,
        video: shape({
            contentId: pt.string,
            sources: arrayOf(pt.object),
            type: CustomPropShapes.oneOfConstant(videoConstant.types)
        })
    };

    static defaultProps = {
        altContentPosition: false
    };

    renderTitles = () => {
        const {title, subtitle} = this.props;

        return (
            <div className={styles.titles}>
                {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
                {title && <span className={styles.title}>{title}</span>}
            </div>
        );
    };

    renderContent = () => {
        const {altContentPosition, button, hasFortniteFont, logoAltText, logoSrc} = this.props;

        const contentClasses = cx(styles.content, {
            [styles.fortnite]: hasFortniteFont,
            [styles.notCentered]: altContentPosition
        });

        const logo = (
            <div className={contentClasses}>
                {logoSrc ? (
                    <div className={styles.logo}>
                        <DynamicLogo
                            type={logoContainerTypes.BANNER}
                            alt={logoAltText}
                            src={getAssetPath(logoSrc)}
                        />
                    </div>
                ) : (
                    this.renderTitles()
                )}

                {button && button.text && (
                    <Button className={styles.button} theme={button.theme}>
                        {button.text}
                    </Button>
                )}
            </div>
        );

        return logo;
    };

    renderBackground = () => {
        const {video, breakpoint, srcSet, title, rating, locale} = this.props;

        const largeOrGreater = [
            breakpoints.name.LARGE,
            breakpoints.name.XLARGE,
            breakpoints.name.MAX
        ].includes(breakpoint);
        const showVideo = largeOrGreater && video;
        const canRenderImage = !showVideo && !!srcSet;
        const imageFallback =
            srcSet &&
            (getAssetPath(srcSet[imageOrientation.HORIZONTAL]) ||
                getAssetPath(srcSet[imageOrientation.VERTICAL]));

        return (
            <Fragment>
                {showVideo && (
                    <div className={styles.videoContainer}>
                        <Video
                            className={styles.video}
                            contentId={video.contentId}
                            sources={video.sources}
                            autoplay
                            loop
                            hasFullScreen={false}
                            hasControls={false}
                            locale={locale}
                            rating={rating}
                            title={title}
                            type={video.type}
                            poster={imageFallback}
                        />
                    </div>
                )}

                {canRenderImage && (
                    <Picture
                        alt={title}
                        srcSet={{
                            [breakpoints.name.XSMALL]: getAssetPath(
                                srcSet[imageOrientation.VERTICAL]
                            ),
                            [breakpoints.name.LARGE]: getAssetPath(
                                srcSet[imageOrientation.HORIZONTAL]
                            )
                        }}
                    />
                )}
            </Fragment>
        );
    };

    render() {
        const {inFeed, isFullBleed, isLauncher} = this.props;

        const wrapperClasses = cx(styles.wrapper, {
            [styles.web]: !isLauncher,
            [styles.fullBleed]: isFullBleed,
            [styles.inFeed]: inFeed,
            [styles.notFeed]: !inFeed
        });

        if (inFeed) {
            return (
                <div className={wrapperClasses}>
                    <div className={styles.inner}>
                        {this.renderContent()}
                        {this.renderBackground()}
                    </div>
                </div>
            );
        }

        return (
            <div className={wrapperClasses}>
                {this.renderContent()}
                {this.renderBackground()}
            </div>
        );
    }
}

/**
 * Additional export with WithWindow for storybook
 * enables breakpoint based functionality in stories
 */
export const wrappedHero = WithWindow(Hero);

export default compose(
    connect(mapStateToProps),
    WithWindow
)(Hero);
